import React, { useState, useEffect, useCallback } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import RTActionEditor from './RTActionEditor';
import ConfirmationDialog from '../popups/ConfirmationDialog';
import Popup from '../popups/popup';
import usePagination from '../utils/usePagination';
import { formatLocalDateTime, formatElapsed } from '../utils/formatDateTime';
import '../assets/AllTabsCommon.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';
import '../assets/NdSessionView.css';

const ROWS_PER_PAGE = 10;

const ACTIVE_COLUMNS = [
  { key: 'sessionId', label: 'Session ID' },
  { key: 'systemId', label: 'System ID' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'duration', label: 'Duration' },
  { key: 'configVersion', label: 'Config Version' },
  { key: 'actionCount', label: 'Actions' },
  { key: 'configSummary', label: 'Configuration' },
  { key: 'rdSessionId', label: 'RD Session' },
];

// Render per-type count chips from actionTypeCounts; fall back to plain text.
function ConfigChips({ session }) {
  const c = session.actionTypeCounts;
  if (!c) return <span>{session.configSummary || '—'}</span>;
  return (
    <div className="rt-config-chips">
      {c.monitor > 0 && <span className="rt-chip rt-chip-blue">👁 {c.monitor}</span>}
      {c.configure > 0 && <span className="rt-chip rt-chip-purple">⚙ {c.configure}</span>}
      {c.run > 0 && <span className="rt-chip rt-chip-green">⚡ {c.run}</span>}
      {c.monitor === 0 && c.configure === 0 && c.run === 0 && <span className="rt-text-muted">—</span>}
    </div>
  );
}

function RTActiveSessions({ userInfo, onNewSession }) {
  const [sessions, setSessions] = useState([]);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmingStop, setConfirmingStop] = useState(false);
  const [message, setMessage] = useState('');

  const loadSessions = useCallback(async () => {
    const data = await realTimeDeviceService.getActiveSessions();
    setSessions(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const filtered = sessions.filter((s) =>
    (s.systemId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const { pageItems, renderPageNumbers } = usePagination(filtered, currentPage, setCurrentPage, ROWS_PER_PAGE);

  const handleRowClick = async (session) => {
    if (expandedSessionId === session.sessionId) {
      setExpandedSessionId(null);
      setExpandedSession(null);
      return;
    }
    const full = await realTimeDeviceService.getSession(session.sessionId, session.systemId);
    setExpandedSession(full || session);
    setExpandedSessionId(session.sessionId);
  };

  const renderCell = (session, colKey) => {
    switch (colKey) {
      case 'sessionId':
        return (
          <span
            className="clickable-session-id"
            onClick={() => handleRowClick(session)}
            style={{ cursor: 'pointer', color: '#1a4fba', textDecoration: 'underline' }}
          >
            {session.sessionId}
          </span>
        );
      case 'startTime':
        return formatLocalDateTime(session.startTime);
      case 'duration':
        return session.durationMins ? `${session.durationMins} min` : 'Open-ended';
      case 'configVersion':
        return session.configVersion != null
          ? <span className="rt-chip rt-chip-version">v{session.configVersion}</span>
          : <span className="rt-text-muted">—</span>;
      case 'configSummary':
        return <ConfigChips session={session} />;
      case 'rdSessionId':
        return session.rdSessionId
          ? <span className="rt-chip rt-chip-amber">⟷ {session.rdSessionId}</span>
          : '—';
      default:
        return session[colKey];
    }
  };

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="data-table-tab-container">
      <div className="rt-page-header" style={{ alignItems: 'center', marginBottom: 16 }}>
        <div className="search-container" style={{ margin: 0, flex: 1, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search by System ID"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="rt-page-header-actions">
          <button className="submit-button" style={{ marginTop: 0 }} onClick={onNewSession}>+ New Session</button>
        </div>
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>{ACTIVE_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr><td colSpan={ACTIVE_COLUMNS.length} className="table-empty">No active sessions</td></tr>
            ) : (
              pageItems.map((session) => (
                <React.Fragment key={session.sessionId}>
                  <tr className={expandedSessionId === session.sessionId ? 'expanded-row' : ''}>
                    {ACTIVE_COLUMNS.map((col) => <td key={col.key}>{renderCell(session, col.key)}</td>)}
                  </tr>
                  {expandedSessionId === session.sessionId && expandedSession && (
                    <tr className="expanded-details-row">
                      <td colSpan={ACTIVE_COLUMNS.length}>
                        <div className="expanded-row-content">
                          <RTSingleSession
                            session={expandedSession}
                            confirmingStop={confirmingStop}
                            setConfirmingStop={setConfirmingStop}
                            message={message}
                            setMessage={setMessage}
                            onSessionChanged={(updated) => setExpandedSession(updated)}
                            onClose={async () => {
                              setExpandedSessionId(null);
                              setExpandedSession(null);
                              setConfirmingStop(false);
                              await loadSessions();
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-controls">{renderPageNumbers()}</div>
    </div>
  );
}

// ── Single-session detail view ───────────────────────────────────────────────
function RTSingleSession({ session, confirmingStop, setConfirmingStop, message, setMessage, onSessionChanged, onClose }) {
  const [elapsedSec, setElapsedSec] = useState(
    session.startTime ? Math.floor((Date.now() - new Date(session.startTime).getTime()) / 1000) : 0
  );

  useEffect(() => {
    const timer = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [session.sessionId]);

  // Local staged actions — changes don't fire the API until the user reviews & applies
  const [localActions, setLocalActions] = useState(session.actions || []);
  const [showReview, setShowReview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  // Diff: what's been added or removed locally vs. original session
  const originalIds = new Set((session.actions || []).map(a => a.id));
  const localIds = new Set(localActions.map(a => a.id));
  const added   = localActions.filter(a => !originalIds.has(a.id));
  const removed = (session.actions || []).filter(a => !localIds.has(a.id));
  const hasPendingChanges = added.length > 0 || removed.length > 0;

  const handleDiscard = () => {
    setLocalActions(session.actions || []);
    setApplyError('');
  };

  const handleApply = async () => {
    setApplying(true);
    setApplyError('');
    try {
      const updated = await realTimeDeviceService.replaceActions(
        session.sessionId, session.systemId, localActions, session
      );
      setShowReview(false);
      if (updated) onSessionChanged({ ...updated, actions: localActions });
    } catch (e) {
      setApplyError('Failed to apply changes. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  // Local-only mutations — no API calls
  const handleAddAction    = (action)         => setLocalActions(prev => [...prev, action]);
  const handleUpdateAction = (actionId, fields) =>
    setLocalActions(prev => prev.map(a => a.id === actionId ? { ...a, ...fields, id: actionId } : a));
  const handleRemoveAction = (actionId)       =>
    setLocalActions(prev => prev.filter(a => a.id !== actionId));

  const handleConfirmStop = async () => {
    setConfirmingStop(false);
    await realTimeDeviceService.stopSession(session.sessionId, session.systemId);
    setMessage('Session stopped successfully.');
  };

  const typeCounts = localActions.reduce(
    (acc, a) => { acc[a.type] = (acc[a.type] || 0) + 1; return acc; },
    {}
  );

  const ACTION_ICONS_MAP = { monitor: '👁️', configure: '⚙️', run: '⚡' };
  const maxLabel = session.durationMins ? `${session.durationMins}:00` : '∞';

  return (
    <div className="data-table-tab-container">
      <button className="go-back-button" onClick={onClose}>✕ Close</button>

      {/* ── Session header card ── */}
      <div className="card rt-card" style={{ marginTop: 16, marginBottom: 16 }}>
        <div className="rt-session-card-top">
          <div className="rt-session-card-title-row">
            <h2 className="rt-page-title" style={{ margin: 0 }}>Active Session</h2>
            <span className="rt-chip rt-chip-version">● Running</span>
            {session.configVersion != null && (
              <span className="rt-chip rt-chip-version">Config v{session.configVersion}</span>
            )}
          </div>
          <button className="submit-button rt-stop-button" style={{ marginTop: 0 }} onClick={() => setConfirmingStop(true)}>
            ⊙ Stop Session
          </button>
        </div>

        <div className="rt-session-card-meta">
          <span>Device: <strong>{session.systemId}</strong></span>
          <span>Session: <strong>{session.sessionId}</strong></span>
          <span>⏱ <strong>{formatElapsed(elapsedSec)}</strong> / {maxLabel}</span>
          {session.durationMins && <span>Duration: <strong>{session.durationMins} min</strong></span>}
        </div>

        {localActions.length > 0 && (
          <div className="rt-config-chips">
            {typeCounts.monitor > 0 && <span className="rt-chip rt-chip-blue">👁 {typeCounts.monitor} Monitor</span>}
            {typeCounts.configure > 0 && <span className="rt-chip rt-chip-purple">⚙ {typeCounts.configure} Configure</span>}
            {typeCounts.run > 0 && <span className="rt-chip rt-chip-green">⚡ {typeCounts.run} Run</span>}
          </div>
        )}
      </div>

      {session.rdSessionId && (
        <div className="rt-rd-banner">
          ⟷ Remote Diagnostics active — {session.rdSessionId}
        </div>
      )}

      {/* Pending changes banner */}
      {hasPendingChanges && (
        <div className="nd-sv-pending-banner" style={{ marginBottom: 12 }}>
          <span>⚠️ <strong>{added.length + removed.length} unsaved change{added.length + removed.length !== 1 ? 's' : ''}</strong> — not yet sent to device</span>
          <div className="nd-sv-pending-banner-actions">
            <button className="nd-sv-pending-discard" onClick={handleDiscard}>Discard</button>
            <button className="nd-sv-pending-review" onClick={() => { setApplyError(''); setShowReview(true); }}>Review &amp; Apply</button>
          </div>
        </div>
      )}

      {applyError && (
        <div className="nd-sv-error-banner" style={{ marginBottom: 12 }}>
          <span>{applyError}</span>
          <button className="nd-sv-error-close" onClick={() => setApplyError('')}>✕</button>
        </div>
      )}

      {message && <Popup message={message} onClose={() => { setMessage(''); if (onClose) onClose(); }} />}

      <RTActionEditor
        actions={localActions}
        onAdd={handleAddAction}
        onRemove={handleRemoveAction}
        onUpdate={handleUpdateAction}
        variant="active"
      />

      {/* Review & Apply modal */}
      {showReview && (
        <div className="nd-sv-review-overlay" onClick={e => { if (e.target === e.currentTarget) setShowReview(false); }}>
          <div className="nd-sv-review-modal">
            <div className="nd-sv-review-modal-header">
              <div>
                <h3>Review Pending Changes</h3>
                <p>{added.length + removed.length} change{added.length + removed.length !== 1 ? 's' : ''} will be sent to the device</p>
              </div>
              <button className="nd-sv-review-close" onClick={() => setShowReview(false)}>✕</button>
            </div>
            <div className="nd-sv-review-modal-body">
              {added.length > 0 && (
                <div>
                  <div className="nd-sv-review-section-label nd-sv-review-section-label--add">➕ Adding ({added.length})</div>
                  {added.map(a => (
                    <div key={a.id} className="nd-sv-review-row nd-sv-review-row--add">
                      <span className="nd-sv-review-row-icon">{a.icon || ACTION_ICONS_MAP[a.type]}</span>
                      <span className="nd-sv-review-row-label">{a.label}</span>
                      {a.sub && <span className="nd-sv-review-row-sub">{a.sub}</span>}
                    </div>
                  ))}
                </div>
              )}
              {removed.length > 0 && (
                <div>
                  <div className="nd-sv-review-section-label nd-sv-review-section-label--remove">➖ Removing ({removed.length})</div>
                  {removed.map(a => (
                    <div key={a.id} className="nd-sv-review-row nd-sv-review-row--remove">
                      <span className="nd-sv-review-row-icon">{a.icon || ACTION_ICONS_MAP[a.type]}</span>
                      <span className="nd-sv-review-row-label">{a.label}</span>
                      {a.sub && <span className="nd-sv-review-row-sub">{a.sub}</span>}
                    </div>
                  ))}
                </div>
              )}
              {applyError && <div className="nd-sv-error-banner"><span>{applyError}</span></div>}
            </div>
            <div className="nd-sv-review-modal-footer">
              <button className="nd-sv-review-cancel" onClick={() => setShowReview(false)} disabled={applying}>Cancel</button>
              <button className="nd-sv-review-apply" onClick={handleApply} disabled={applying}>
                {applying ? 'Applying…' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingStop && (
        <ConfirmationDialog
          message="Are you sure you want to stop this session?"
          onConfirm={handleConfirmStop}
          onCancel={() => setConfirmingStop(false)}
        />
      )}
    </div>
  );
}

export default RTActiveSessions;
